/*
 * Copyright (c) 2026. Bluemate B.V.
 *
 * Dedicated install-state service: "is the Bluemate PWA installed on THIS
 * device?" — the single detection module for logic that must react to the
 * (un)installed state, e.g. the mobile push-subscription guard. Deliberately
 * independent of the install banners (pwaBanners): consumers get raw install
 * detection, never banner visibility state.
 */

import {hasInstallPrompt, isPWAInstalled, isStandalone} from './pwaUtil';

interface NavigatorWithRelatedApps extends Navigator {
    getInstalledRelatedApps?: () => Promise<Array<unknown>>;
}

/** True when the Bluemate PWA is installed on this device, best-effort:
 *  - standalone display mode → we ARE the installed app;
 *  - a non-empty `navigator.getInstalledRelatedApps()` → authoritative
 *    "installed" on Chromium Android. This is the ONLY signal a browser TAB
 *    has: an installed app no longer fires `beforeinstallprompt`, so the
 *    absence of a prompt says nothing. It needs the self-referencing
 *    `related_applications` entry in manifest.webmanifest — without it
 *    Chromium always returns []. An EMPTY result is still not treated as
 *    proof of "not installed": every other browser lacks the API entirely;
 *  - otherwise the persisted "installed" flag. That flag is written ONLY by a
 *    launch in standalone display mode (the one thing a mere home-screen
 *    shortcut can never produce — Chrome fires `appinstalled` for its "create
 *    shortcut" branch as well), expires after a day, and is cleared as soon as
 *    the browser offers `beforeinstallprompt` again (i.e. after an uninstall). */
export async function isPwaInstalledOnThisDevice(): Promise<boolean> {
    if (isStandalone()) return true;
    try {
        const related = await (navigator as NavigatorWithRelatedApps).getInstalledRelatedApps?.();
        if (Array.isArray(related) && related.length > 0) return true;
    } catch {
        /* API unsupported — fall through to the persisted flag */
    }
    return isPWAInstalled();
}

/** Positive proof that the app is NOT installed here, as opposed to the mere
 *  absence of proof that it is. The browser is holding an unspent
 *  `beforeinstallprompt`, which Chromium fires only while the origin is
 *  installable — it withholds it once installed, and both `appinstalled` and a
 *  spent prompt clear the latch. `false` means "no information" (iOS Safari
 *  never fires it at all), never "installed", so callers must not read it as
 *  the negation of `isPwaInstalledOnThisDevice()`. */
export function isPwaConfirmedNotInstalled(): boolean {
    return hasInstallPrompt();
}
